import { Injectable } from '@angular/core';
import { LayersStreams } from '../../common/current-layers/layers-streams.service';
import { MapWorkspacesStoreService } from '../../common/current-map-workspaces/map-workspaces-store.service';
import { DateUtils } from '../../common/utility/date-utils';
import { UnitConversionService } from '../../common/utility/unit-conversion.service';
import { AutoFieldModelType, FieldType } from '../../template-services/field';

@Injectable({
    providedIn: 'root'
})
export class FeaturePropertyService {
    constructor(
        private unitConversionService: UnitConversionService,
        private layersStreams: LayersStreams,
        private mapWorkspacesStore: MapWorkspacesStoreService
    ) {}

    // Feature Property Transformer - It handles specific field types like Date, Angle, and Length, applying appropriate formatting
    public propertyValueTransformer(
        propertyValue: string | boolean,
        layerId: string,
        selectedPropertyName: string
    ): string {
        if (propertyValue === undefined || !layerId || !selectedPropertyName) return null;

        // Grab fields from cachedLayerFields, if not available, use LayerFields from sessionStorage
        const fields =
            this.layersStreams.cachedLayerFields?.[layerId] ||
            JSON.parse(sessionStorage.getItem('cachedLayerFields'))?.[layerId];

        const selectedField = fields?.find((field: { name: string }) => field.name === selectedPropertyName);

        if (!selectedField) return null;

        // YesNo / Boolean - Fallback with empty string
        if (selectedField.type === FieldType.YesNo || typeof propertyValue === 'boolean' || propertyValue === null) {
            propertyValue = propertyValue?.toString();
            return this.formatValueToYesNo(propertyValue);
        }

        // Number with Field / Autofield with unit - Fallback with WorkspaceUnit
        if (
            (selectedField.type === FieldType.Number && (selectedField.angleUnit || selectedField.distanceUnit)) ||
            (selectedField.type === FieldType.Autofield && selectedField.hasOwnProperty('unit'))
        ) {
            return this.formatValueWithUnit(propertyValue, selectedField);
        }

        // Date
        if (selectedField.type === FieldType.Date && typeof propertyValue === 'string')
            return DateUtils.formatDateFromDate(propertyValue, 'medium');

        // Collected DateTime - Autofield with isUTC
        if (
            selectedField.type === FieldType.Autofield &&
            (selectedField.modelType === AutoFieldModelType.CreationDateTime ||
                selectedField.modelType === AutoFieldModelType.UpdateDateTime)
        )
            return DateUtils.formatDateFromDateTime(propertyValue, 'mediumdatetime12hr', selectedField.isUTC);

        // Default - Text and other types
        return propertyValue as string;
    }

    private formatValueWithUnit(
        propertyValue: string | boolean,
        selectedField: { modelType: AutoFieldModelType; unit: string; angleUnit: string; distanceUnit: string }
    ): string {
        if (selectedField.modelType) {
            selectedField.unit = selectedField?.unit || this.getWorkspaceUnit(selectedField.modelType);
        }

        const displayUnit = this.unitConversionService.convertUnitToHtml(
            selectedField?.angleUnit || selectedField?.distanceUnit || selectedField.unit?.toLowerCase()
        );

        return propertyValue ? `${propertyValue}${displayUnit}` : null;
    }

    private formatValueToYesNo(propertyValue: string | boolean): string {
        return propertyValue === 'true' ? 'Yes' : propertyValue === 'false' ? 'No' : null;
    }

    private getWorkspaceUnit(selectedFieldType: AutoFieldModelType): string {
        const currentMapWorkspace = this.mapWorkspacesStore.getCurrentMapWorkspace();
        if (currentMapWorkspace.noProjection()) return;

        switch (selectedFieldType) {
            case AutoFieldModelType.XPosition:
            case AutoFieldModelType.YPosition:
                return currentMapWorkspace.coordinateSystem.horizontalUnit;
            case AutoFieldModelType.ZPosition:
                return currentMapWorkspace.coordinateSystem.verticalUnit;
            default:
                return;
        }
    }
}
